import axios from "axios";

/**
 * Base API Service Class
 *
 * Provides common functionality for all API services including:
 * - Automatic cookie-based authentication
 * - Centralized error handling
 * - Request/response interceptors
 * - Base URL configuration
 *
 * @class BaseApiService
 */
class BaseApiService {
  constructor() {
    this.baseURL = `${process.env.REACT_APP_FIREBASE_API_HOST}/api`;
    this.axiosInstance = this.createAxiosInstance();
  }

  /**
   * Creates and configures axios instance with interceptors
   * @returns {AxiosInstance} Configured axios instance
   */
  createAxiosInstance() {
    const instance = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      withCredentials: true, // Include cookies in requests
    });

    // Response interceptor for error handling
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        // If we get 401 (unauthorized), redirect to login
        // Backend should handle token refresh automatically via cookies
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          if (!requestUrl.includes('/auth/authenticate')) {
            console.error("Authentication failed - redirecting to login");
            if (typeof window !== "undefined") {
              window.location.href = "/login";
            }
          }
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }

  /**
   * Generic GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Additional axios config
   * @returns {Promise<any>} API response data
   */
  async get(endpoint, config = {}) {
    try {
      const response = await this.axiosInstance.get(endpoint, config);
      return response.data;
    } catch (error) {
      this.handleError(error, "GET", endpoint);
      throw error;
    }
  }

  /**
   * Generic POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional axios config
   * @returns {Promise<any>} API response data
   */
  async post(endpoint, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.post(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error, "POST", endpoint);
      throw error;
    }
  }

  /**
   * Generic PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional axios config
   * @returns {Promise<any>} API response data
   */
  async put(endpoint, data = {}, config = {}) {
    try {
      const response = await this.axiosInstance.put(endpoint, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error, "PUT", endpoint);
      throw error;
    }
  }

  /**
   * Generic DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Additional axios config
   * @returns {Promise<any>} API response data
   */
  async delete(endpoint, config = {}) {
    try {
      const response = await this.axiosInstance.delete(endpoint, config);
      return response.data;
    } catch (error) {
      this.handleError(error, "DELETE", endpoint);
      throw error;
    }
  }

  /**
   * Centralized error handling
   * @param {Error} error - The error object
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   */
  handleError(error, method, endpoint) {
    const errorInfo = {
      method,
      endpoint,
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      timestamp: new Date().toISOString(),
    };

    console.error("API Error:", errorInfo);

    // You can extend this to send errors to monitoring service
    // Example: ErrorReportingService.reportError(errorInfo);
  }

  /**
   * Helper method to get timezone offset for API requests
   * @returns {string} Timezone offset string (e.g., "+02", "-05")
   */
  getTimezoneOffset() {
    const timezoneOffset = new Date().getTimezoneOffset() / -60;
    return timezoneOffset >= 0
      ? `+${String(Math.abs(timezoneOffset)).padStart(2, "0")}`
      : `-${String(Math.abs(timezoneOffset)).padStart(2, "0")}`;
  }
}

export default BaseApiService;
